import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Loader } from 'lucide-react';
import { Artifact } from '../utils/parseArtifacts';
import { parseArtifacts, formatArtifactTitle } from '../utils/parseArtifacts';
import { useParams } from 'react-router-dom';
import api from '../utils/axios';
import { Icon } from './Icon/Icon';
import { IconName } from './Icon/Icon.type';
import { BGLoadingIndicator } from './BGLoadingIndicator/BGLoadingIndicator';

interface ArtifactDetailProps {
  artifact: Artifact | null;
  isOpen: boolean;
  onClose: () => void;
}

interface ArtifactWithContent extends Artifact {
  contentLoaded?: boolean;
}


const codeBlockClass = "bg-gray-100 text-gray-800 font-mono p-4 rounded-lg overflow-x-auto text-sm";

const ArtifactDetail: React.FC<ArtifactDetailProps> = ({ artifact, isOpen, onClose }) => {
  const [viewMode, setViewMode] = useState<'html' | 'code'>('html');
  const [iframeKey, setIframeKey] = useState(0);
  const [openingInNewTab, setOpeningInNewTab] = useState(false);
  const [isArtifactDownloading, setIsArtifactDownloading] = useState(false);
  const [artifactWithContent, setArtifactWithContent] = useState<ArtifactWithContent | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { projectId } = useParams<{ projectId: string }>();

  // Create a stable identifier using artifactId-messageId to prevent unnecessary refetches
  const artifactIdentifier = artifact 
    ? `${artifact.identifier || (artifact as any).id || (artifact as any)._id || ''}-${(artifact as any).messageId || ''}`
    : null;

  // Fetch artifact content when artifact changes and content is not loaded
  useEffect(() => {
    const fetchArtifactContent = async () => {
      if (!artifact || !isOpen) {
        setArtifactWithContent(null);
        return;
      }

      // If artifact already has content, use it directly
      if (artifact.content && artifact.content.trim()) {
        setArtifactWithContent({ ...artifact, contentLoaded: true });
        return;
      }

      // Otherwise, fetch content from API
      try {
        setLoadingContent(true);
        const artifactId = (artifact as any).id || (artifact as any)._id;
        
        const response = await api.get(`/requirement-groups/projects/${projectId}/artifacts/${artifactId}/content`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });

        setArtifactWithContent({
          ...artifact,
          content: response.data.content || '',
          contentLoaded: true
        });
      } catch (error) {
        console.error('Failed to fetch artifact content:', error);
        // Set empty content on error
        setArtifactWithContent({
          ...artifact,
          content: '',
          contentLoaded: true
        });
      } finally {
        setLoadingContent(false);
      }
    };

    fetchArtifactContent();
  }, [artifactIdentifier, isOpen]);

  // Update content if the artifact content changes (e.g., during streaming)
  // but only if it's the same artifact (same artifactId-messageId)
  useEffect(() => {
    if (!artifact || !artifactWithContent) return;
    
    // Check if this is the same artifact using artifactId-messageId
    const currentIdentifier = `${artifact.identifier || (artifact as any).id || (artifact as any)._id || ''}-${(artifact as any).messageId || ''}`;
    const loadedIdentifier = `${artifactWithContent.identifier || (artifactWithContent as any).id || (artifactWithContent as any)._id || ''}-${(artifactWithContent as any).messageId || ''}`;
    
    if (currentIdentifier === loadedIdentifier && artifact.content && artifact.content !== artifactWithContent.content) {
      // Update the content without triggering a full reload
      setArtifactWithContent({ ...artifact, contentLoaded: true });
    }
  }, [artifact?.content]);

  // If artifact.content contains an <artifact> tag, parse it and use the inner content/type/title
  let parsedArtifact: Artifact | null = null;
  if (artifactWithContent && artifactWithContent.content && /<artifact[\s>]/.test(artifactWithContent.content)) {
    const { parts } = parseArtifacts(artifactWithContent.content);
    for (const part of parts) {
      if (typeof part !== 'string') {
        parsedArtifact = part;
        break;
      }
    }
  }
  
  // Preserve the original artifact's id and _id when using parsed artifact
  // This ensures saved artifacts are recognized as saved even after parsing
  const displayArtifact = parsedArtifact 
    ? { 
        ...parsedArtifact, 
        id: (artifactWithContent as any)?.id,
        _id: (artifactWithContent as any)?._id,
        messageId: (artifactWithContent as any)?.messageId
      } 
    : artifactWithContent;

  useEffect(() => {
    // Reset iframe srcdoc when artifact or viewMode changes
    if (iframeRef.current && displayArtifact && viewMode === 'html') {
      iframeRef.current.srcdoc = displayArtifact.content;
    }
  }, [displayArtifact, viewMode]);

  const handleHomeClick = () => {
    // For HTML artifacts, reload the iframe to go back to the main page
    if (displayArtifact?.type === 'text/html') {
      setIframeKey(prev => prev + 1); // Force iframe reload by changing key
    }
  };

  const downloadArtifact = async () =>
  {
    try {
      setIsArtifactDownloading(true);
      
      if (!displayArtifact) {
        throw new Error('Artifact not available for download');
      }
      
      // Use the unified download-with-temp endpoint for all artifacts
      const messageId = (displayArtifact as any).messageId || `artifact_${displayArtifact.identifier}`;
      
      const response = await api.post(`/projects/${projectId}/artifacts/download-with-temp`, {
        artifactId: displayArtifact.identifier,
        messageId: messageId,
        title: displayArtifact.title,
        content: displayArtifact.content,
        type: displayArtifact.type,
        version: displayArtifact.version // Include version when downloading
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.headers['content-disposition'].split(';')[1].split('=')[1];
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download artifact. Please try again.');
    } finally {
      setIsArtifactDownloading(false);
    }
  };

  const handleOpenInNewTab = async () => {
    if (!displayArtifact || !projectId) {
      return;
    }

    try {
      setOpeningInNewTab(true);
      
      // Check if this is a saved artifact (has an id field)
      const isSaved = !!(displayArtifact as any).id || !!(displayArtifact as any)._id;
      let artifactId: string;

      if (isSaved) {
        // For saved artifacts, use the database ID directly
        artifactId = (displayArtifact as any).id || (displayArtifact as any)._id;
      } else {
        // For chat artifacts, create or find temporary artifact
        // We need a messageId - for artifacts in right panel, we might not have one
        // So we'll use the artifact identifier as a fallback
        const messageId = (displayArtifact as any).messageId || `artifact_${displayArtifact.identifier}`;
        
        const response = await api.post(`/projects/${projectId}/artifacts/temp-for-view`, {
          artifactId: displayArtifact.identifier,
          messageId: messageId,
          title: displayArtifact.title,
          content: displayArtifact.content,
          type: displayArtifact.type,
          version: displayArtifact.version // Include version when creating temp artifact
        }, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        
        artifactId = response.data.id || response.data._id;
      }

      if (artifactId) {
        const url = `/project/${projectId}/artifact/${artifactId}`;
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error opening artifact in new tab:', error);
      alert('Failed to open artifact in new tab. Please try again.');
    } finally {
      setOpeningInNewTab(false);
    }
  };

  if (!isOpen) return null;

  // Show loading state while fetching content
  if (loadingContent || !displayArtifact) {
    return (
      <div className="flex flex-col h-full w-full bg-bg-primary border-l border-border-primary">
        <div className="flex items-center justify-between px-bg-v2-lg py-bg-v2-lg bg-bg-primary border-b border-border-primary h-[60px] relative">
          <div className="flex items-center gap-3">
            <Icon name={IconName.Document} size="md" className="text-fg-secondary" inheritColor />
            <h2 className="text-sm font-semibold text-text-primary">Loading...</h2>
          </div>
          <div className="flex items-center gap-0">
            <button
              onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-fg-secondary hover:bg-bg-secondary transition-all duration-200"
            title="Close"
          >
            <Icon name={IconName.Cross} size="md" inheritColor />
          </button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center bg-bg-primary">
          <BGLoadingIndicator size="md" />
        </div>
      </div>
    );
  }

  const isHtml = displayArtifact.type === 'text/html';
  const isMarkdown = displayArtifact.type === 'text/markdown';
  const isPlain = displayArtifact.type === 'text/plain';

  // Remove first heading if it matches artifact.title for markdown
  let markdownContent = displayArtifact.content;
  if (isMarkdown && displayArtifact.content) {
    const lines = displayArtifact.content.split(/\r?\n/);
    if (lines[0].trim().replace(/^#+\s*/, '') === displayArtifact.title.trim()) {
      markdownContent = lines.slice(1).join('\n');
    }
  }

  const displayTitle = formatArtifactTitle(displayArtifact.title, displayArtifact.version);

  return (
    <div className="flex flex-col h-full w-full bg-bg-primary border-l border-border-primary">
      {/* Header */}
      <div className="flex items-center justify-between px-bg-v2-lg py-bg-v2-lg bg-bg-primary border-b border-border-primary h-[60px] relative">
        <div className="flex items-center gap-3">
          {/* Document Icon */}
          <Icon name={IconName.Document} size="md" className="text-fg-secondary" inheritColor />
          <h2 className="text-sm font-semibold text-text-primary" title={displayTitle}>{displayTitle}</h2>
        </div>
        
        {/* Center Section - HTML/Code Toggle */}
        <div className="absolute left-1/2 transform -translate-x-1/2">
          {isHtml && (
            <div className="flex items-center border border-border-primary rounded-md overflow-hidden h-8">
              <button
                className={`px-3 h-full text-xs font-medium transition-colors border-r border-border-primary ${
                  viewMode === 'html' 
                    ? 'bg-bg-tertiary text-text-secondary' 
                    : 'bg-bg-primary text-text-secondary hover:text-text-primary'
                }`}
                onClick={() => setViewMode('html')}
              >
                HTML
              </button>
              <button
                className={`px-3 h-full text-xs font-medium transition-colors ${
                  viewMode === 'code' 
                    ? 'bg-bg-tertiary text-text-secondary' 
                    : 'bg-bg-primary text-text-secondary hover:text-text-primary'
                }`}
                onClick={() => setViewMode('code')}
              >
                Code
              </button>
            </div>
          )}
        </div>
        
        {/* Right Section - Action Icons */}
        <div className="flex items-center gap-0">
          {/* Download Icon */}
          {!isArtifactDownloading ? (
            <button
              onClick={downloadArtifact}
              disabled={isArtifactDownloading}
              className="w-8 h-8 flex items-center justify-center rounded-full text-fg-secondary hover:bg-bg-primary-hover transition-colors disabled:opacity-50"
              title="Download"
            >
              <Icon name={IconName.Download} size="md" inheritColor />
            </button>
          ) : (
            <div className="w-8 h-8 flex items-center justify-center">
              <Loader className="animate-spin h-4 w-4 text-fg-secondary" />
            </div>
          )}
          
          {/* Open in New Tab Icon */}
          <button
            onClick={handleOpenInNewTab}
            disabled={openingInNewTab}
            className="w-8 h-8 flex items-center justify-center rounded-full text-fg-secondary hover:bg-bg-primary-hover transition-colors disabled:opacity-50"
            title="Open in new tab"
          >
            {openingInNewTab ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-fg-secondary"></div>
            ) : (
              <Icon name={IconName.ExternalLink} size="md" inheritColor />
            )}
          </button>
          
          {/* Close Icon */}
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-fg-secondary hover:bg-bg-primary-hover transition-all duration-200"
            title="Close"
          >
            <Icon name={IconName.Cross} size="md" inheritColor />
          </button>
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isPlain && (
          <div className="prose prose-lg max-w-none p-6">
            {displayArtifact.content}
          </div>
        )}
        {isMarkdown && (
          <div className="prose prose-lg max-w-none p-6">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ node, href, children, ...props }: any) => {
                  return (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      {...props}
                    >
                      {children}
                    </a>
                  );
                }
              }}
            >
              {markdownContent}
            </ReactMarkdown>
          </div>
        )}
        {isHtml && (
          <div className="w-full h-full">
            {viewMode === 'html' ? (
              <iframe
                key={iframeKey}
                ref={iframeRef}
                title="HTML Document Preview"
                srcDoc={displayArtifact.content}
                className="w-full h-full border-0 bg-white"
                sandbox="allow-scripts allow-same-origin"
              />
            ) : (
              <div className="p-6">
                <pre className={codeBlockClass}>
                  <code>{displayArtifact.content}</code>
                </pre>
              </div>
            )}
          </div>
        )}
        {!isPlain && !isMarkdown && !isHtml && (
          <div className="prose prose-lg max-w-none p-6">
            <pre className={codeBlockClass}>
              <code>{displayArtifact.content}</code>
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtifactDetail;